import {
  BadgeCheck, Bookmark, Globe2, Heart, MessageCircle, MoreHorizontal, Music2, Plus, Repeat2, Send, Share2, ThumbsUp,
} from "lucide-react";
import { Img } from "@/components/common";
import { Screen } from "./Frames";

const Avatar = ({ ring, size = "h-9 w-9", rounded = "rounded-full" }) => (
  <span className={`${ring ? "bg-[conic-gradient(from_200deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5,#feda75)] p-[2px]" : ""} ${rounded} inline-block shrink-0`}>
    <span className={`${size} ${rounded} grid place-items-center border-2 border-white bg-[#0a0b0c] font-display text-base text-[#d6b262]`}>L</span>
  </span>
);

const Card = ({ children, testId, max = "max-w-[440px]" }) => (
  <article data-testid={testId} className={`mx-auto w-full ${max} overflow-hidden rounded-xl border border-black/10 bg-white text-[#111] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.55)]`}>
    {children}
  </article>
);

export const InstagramPost = ({ src, alt, caption, meta = {} }) => (
  <Card testId="frame-social-instagram">
    <header className="flex items-center gap-3 px-3 py-2.5">
      <Avatar ring />
      <div className="leading-tight">
        <p className="flex items-center gap-1 text-[13px] font-semibold">lutazzi <BadgeCheck className="h-3.5 w-3.5 fill-[#0095f6] text-white" aria-hidden /></p>
        <p className="text-[11px] text-black/60">{meta.location || "Miami, Florida"}</p>
      </div>
      <MoreHorizontal className="ml-auto h-5 w-5" aria-hidden />
    </header>
    <Screen src={src} alt={alt} ratio="1/1" position="center" />
    <div className="px-3 pb-3 pt-2.5 text-[13px]">
      <div className="flex items-center gap-4" aria-hidden>
        <Heart className="h-6 w-6 fill-[#ff3040] text-[#ff3040]" /><MessageCircle className="h-6 w-6" /><Send className="h-6 w-6" />
        <Bookmark className="ml-auto h-6 w-6" />
      </div>
      <p className="mt-2 font-semibold">{meta.likes || "8,204"} likes</p>
      <p className="mt-1"><span className="font-semibold">lutazzi</span> {caption}</p>
      <p className="mt-1 text-black/50">View all {meta.comments || "120"} comments</p>
      <p className="mt-1 text-[10px] uppercase tracking-wide text-black/40">2 hours ago</p>
    </div>
  </Card>
);

export const FacebookPost = ({ src, alt, caption, meta = {} }) => (
  <Card testId="frame-social-facebook" max="max-w-[520px]">
    <header className="flex items-center gap-2.5 px-4 pt-3">
      <Avatar />
      <div className="leading-tight">
        <p className="text-[14px] font-semibold">Lutazzi</p>
        <p className="flex items-center gap-1 text-[12px] text-black/55">Sponsored · <Globe2 className="h-3 w-3" aria-hidden /></p>
      </div>
      <MoreHorizontal className="ml-auto h-5 w-5 text-black/60" aria-hidden />
    </header>
    <p className="px-4 py-3 text-[14px] leading-snug">{caption}</p>
    <Screen src={src} alt={alt} ratio="3/2" position="center" />
    <div className="flex items-center justify-between px-4 py-2 text-[13px] text-black/60">
      <span className="flex items-center gap-1.5">
        <span className="flex -space-x-1" aria-hidden>
          <span className="grid h-[18px] w-[18px] place-items-center rounded-full bg-[#1877f2] ring-2 ring-white"><ThumbsUp className="h-2.5 w-2.5 fill-white text-white" /></span>
          <span className="grid h-[18px] w-[18px] place-items-center rounded-full bg-[#f33e58] ring-2 ring-white"><Heart className="h-2.5 w-2.5 fill-white text-white" /></span>
        </span>
        {meta.likes || "1.2K"}
      </span>
      <span>{meta.comments || "64"} comments · {meta.shares || "21"} shares</span>
    </div>
    <div className="mx-4 grid grid-cols-3 border-t border-black/10 py-1 text-[13px] font-semibold text-black/60" aria-hidden>
      <span className="flex items-center justify-center gap-2 py-1.5"><ThumbsUp className="h-4 w-4" /> Like</span>
      <span className="flex items-center justify-center gap-2 py-1.5"><MessageCircle className="h-4 w-4" /> Comment</span>
      <span className="flex items-center justify-center gap-2 py-1.5"><Share2 className="h-4 w-4" /> Share</span>
    </div>
  </Card>
);

export const LinkedInPost = ({ src, alt, caption, meta = {} }) => (
  <Card testId="frame-social-linkedin" max="max-w-[520px]">
    <header className="flex items-start gap-2.5 px-4 pt-3">
      <Avatar size="h-11 w-11" />
      <div className="leading-tight">
        <p className="text-[14px] font-semibold">Lutazzi <span className="font-normal text-black/50">· 1st</span></p>
        <p className="text-[12px] text-black/60">Creative Director & Technologist</p>
        <p className="flex items-center gap-1 text-[12px] text-black/50">1d · <Globe2 className="h-3 w-3" aria-hidden /></p>
      </div>
      <span className="ml-auto flex items-center gap-1 text-[14px] font-semibold text-[#0a66c2]"><Plus className="h-4 w-4" aria-hidden /> Follow</span>
    </header>
    <p className="px-4 py-3 text-[14px] leading-snug">{caption} <span className="text-black/50">…more</span></p>
    <Screen src={src} alt={alt} ratio="3/2" position="center" />
    <div className="flex items-center justify-between px-4 py-2 text-[12px] text-black/60">
      <span className="flex items-center gap-1"><span className="grid h-4 w-4 place-items-center rounded-full bg-[#378fe9]" aria-hidden><ThumbsUp className="h-2.5 w-2.5 fill-white text-white" /></span>{meta.likes || "980"}</span>
      <span>{meta.comments || "32"} comments · {meta.reposts || "40"} reposts</span>
    </div>
    <div className="mx-2 grid grid-cols-4 border-t border-black/10 py-1 text-[12px] font-semibold text-black/60" aria-hidden>
      <span className="flex items-center justify-center gap-1.5 py-2"><ThumbsUp className="h-4 w-4" /> Like</span>
      <span className="flex items-center justify-center gap-1.5 py-2"><MessageCircle className="h-4 w-4" /> Comment</span>
      <span className="flex items-center justify-center gap-1.5 py-2"><Repeat2 className="h-4 w-4" /> Repost</span>
      <span className="flex items-center justify-center gap-1.5 py-2"><Send className="h-4 w-4" /> Send</span>
    </div>
  </Card>
);

const RailItem = ({ icon: Icon, label, fill }) => (
  <span className="flex flex-col items-center gap-1 text-[11px] font-semibold text-white drop-shadow">
    <Icon className={`h-7 w-7 ${fill || ""}`} aria-hidden />
    {label}
  </span>
);

export const TikTokPost = ({ src, alt, caption, meta = {} }) => (
  <article data-testid="frame-social-tiktok" className="relative mx-auto w-full max-w-[340px] overflow-hidden rounded-[22px] bg-black shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10" style={{ aspectRatio: "9/16" }}>
    <Img src={src} alt={alt} sizes="340px" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" aria-hidden />
    <div className="absolute inset-x-0 top-3 flex justify-center gap-5 text-[14px] font-semibold text-white/70" aria-hidden>
      <span>Following</span>
      <span className="border-b-2 border-white pb-1 text-white">For You</span>
    </div>
    <div className="absolute bottom-20 right-2.5 flex flex-col items-center gap-4">
      <span className="relative mb-2" aria-hidden>
        <Avatar size="h-11 w-11" />
        <span className="absolute -bottom-2 left-1/2 grid h-5 w-5 -translate-x-1/2 place-items-center rounded-full bg-[#fe2c55]"><Plus className="h-3 w-3 text-white" /></span>
      </span>
      <RailItem icon={Heart} label={meta.likes || "92K"} fill="fill-white" />
      <RailItem icon={MessageCircle} label={meta.comments || "1.1K"} fill="fill-white" />
      <RailItem icon={Bookmark} label={meta.saves || "8K"} fill="fill-white" />
      <RailItem icon={Share2} label={meta.shares || "2K"} />
    </div>
    <div className="absolute bottom-4 left-3 right-16 text-white">
      <p className="text-[14px] font-semibold">@lutazzi</p>
      <p className="mt-1 text-[13px] leading-snug">{caption}</p>
      <p className="mt-2 flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[12px]"><Music2 className="h-3.5 w-3.5 shrink-0" aria-hidden /> original sound — lutazzi</p>
    </div>
    <span className="absolute bottom-4 right-3 h-9 w-9 animate-[spin_6s_linear_infinite] rounded-full border-[6px] border-[#222] bg-[#d6b262]" aria-hidden />
  </article>
);
